/**
 * @module AdaptersPage
 * @package @geenius/adapters/solidjs-css
 * @description Renders the vanilla CSS adapters overview for the SolidJS CSS
 * package. The page summarizes adapter health, supports status filtering, and
 * provides an accessible modal workflow for configuring each adapter domain.
 */

import type {
  AdapterDomain,
  AdapterStatusType,
  DomainAdapterConfig,
} from "@geenius/adapters";
import { ADAPTER_DOMAINS, ADAPTER_STATUSES } from "@geenius/adapters";
import type { JSX } from "solid-js";
import {
  createEffect,
  createSignal,
  createUniqueId,
  ErrorBoundary,
  For,
  onCleanup,
  Show,
} from "solid-js";
import { AdapterConfigForm } from "../components/AdapterConfigForm";
import { AdapterList } from "../components/AdapterList";
import { AdapterStatusBadge } from "../components/AdapterStatusBadge";
import { cx } from "../lib/cx";
import { createAdapters } from "../primitives";
import { PageBoundaryFallback } from "./PageBoundaryFallback";

const DIALOG_FOCUSABLE_SELECTORS =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

type RegistryEntry = {
  domain: AdapterDomain;
  provider: string;
  status: AdapterStatusType;
};

/**
 * Props accepted by the SolidJS CSS adapters overview page.
 *
 * @property onNavigateDetail Optional callback used instead of opening the inline modal
 * @property onConfigure Optional callback invoked with submitted adapter configuration. Mirrors the SolidJS reference; lets the consumer persist values per-domain.
 * @property configByDomain Existing per-domain configuration values used to hydrate modal forms.
 * @property class Optional class merged onto the page root
 * @property registryEntries Optional adapter registry snapshot rendered above the grid
 */
export interface AdaptersPageProps {
  onNavigateDetail?: (domain: AdapterDomain) => void;
  onConfigure?: (
    domain: AdapterDomain,
    config: DomainAdapterConfig,
  ) => Promise<void> | void;
  configByDomain?: Partial<Record<AdapterDomain, DomainAdapterConfig>>;
  class?: string;
  registryEntries?: RegistryEntry[];
}

/**
 * Returns focusable descendants for an active dialog container.
 *
 * @param container Dialog container used to scope the focus trap
 * @returns Interactive descendants that should participate in tab order
 */
function getDialogFocusableElements(container: HTMLElement): HTMLElement[] {
  return Array.from(
    container.querySelectorAll<HTMLElement>(DIALOG_FOCUSABLE_SELECTORS),
  );
}

/**
 * Renders the adapter management dashboard for the SolidJS CSS variant.
 *
 * @param props Page props controlling navigation overrides and registry data
 * @returns A dashboard with status filters, adapter cards, and a modal editor
 * @example
 * ```tsx
 * <AdaptersPage onNavigateDetail={(domain) => navigate(`/adapters/${domain}`)} />
 * ```
 */
export function AdaptersPage(props: AdaptersPageProps): JSX.Element {
  return (
    <ErrorBoundary
      fallback={(error, reset) => (
        <PageBoundaryFallback
          class={props.class}
          error={error}
          onRetry={reset}
          title="Adapters failed to load"
        />
      )}
    >
      <AdaptersPageContent {...props} />
    </ErrorBoundary>
  );
}

function AdaptersPageContent(props: AdaptersPageProps): JSX.Element {
  const ctx = createAdapters();
  const [selectedDomain, setSelectedDomain] =
    createSignal<AdapterDomain | null>(null);
  const [filterStatus, setFilterStatus] = createSignal<
    AdapterStatusType | undefined
  >();
  const modalTitleId = createUniqueId();
  let modalRef: HTMLDivElement | undefined;
  const registryEntries = () => props.registryEntries ?? [];

  const connected = () =>
    ADAPTER_DOMAINS.filter((d) => ctx.statuses[d]?.status === "connected")
      .length;
  const errors = () =>
    ADAPTER_DOMAINS.filter((d) => ctx.statuses[d]?.status === "error").length;

  createEffect(() => {
    if (!selectedDomain() || !modalRef) {
      return;
    }

    const dialog = modalRef;
    const ownerDocument = dialog.ownerDocument;
    const ownerWindow = ownerDocument.defaultView;
    const HTMLElementCtor = ownerWindow?.HTMLElement;
    const previousActiveElement =
      HTMLElementCtor && ownerDocument.activeElement instanceof HTMLElementCtor
        ? ownerDocument.activeElement
        : null;
    const focusableElements = getDialogFocusableElements(dialog);
    const firstFocusableElement = focusableElements[0];
    const lastFocusableElement = focusableElements.at(-1);

    (firstFocusableElement ?? dialog).focus();

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        event.preventDefault();
        setSelectedDomain(null);
        return;
      }

      if (event.key !== "Tab") {
        return;
      }

      const activeElement = ownerDocument.activeElement;

      if (!firstFocusableElement || !lastFocusableElement) {
        event.preventDefault();
        dialog.focus();
        return;
      }

      if (event.shiftKey && activeElement === firstFocusableElement) {
        event.preventDefault();
        lastFocusableElement.focus();
        return;
      }

      if (!event.shiftKey && activeElement === lastFocusableElement) {
        event.preventDefault();
        firstFocusableElement.focus();
      }
    };

    ownerDocument.addEventListener("keydown", handleKeyDown);

    onCleanup(() => {
      ownerDocument.removeEventListener("keydown", handleKeyDown);
      previousActiveElement?.focus();
    });
  });

  return (
    <Show
      when={!ctx.isLoading}
      fallback={
        <div
          class={cx(
            "gn-adapters-page",
            "gn-adapters-page--wide",
            "gn-adapters-page--loading",
            props.class,
          )}
          role="status"
          aria-label="Loading adapters"
        >
          <div class="gn-adapters-loading">
            <div class="gn-adapters-skeleton gn-adapters-skeleton--title" />
            <div class="gn-adapters-skeleton gn-adapters-skeleton--subtitle" />
            <div class="gn-adapters-summary">
              <For each={[1, 2, 3]}>
                {() => (
                  <div class="gn-adapters-skeleton gn-adapters-skeleton--summary" />
                )}
              </For>
            </div>
            <div class="gn-adapters-grid">
              <For each={[1, 2, 3, 4]}>
                {() => (
                  <div class="gn-adapters-skeleton gn-adapters-skeleton--card" />
                )}
              </For>
            </div>
          </div>
        </div>
      }
    >
      <div
        class={cx("gn-adapters-page", "gn-adapters-page--wide", props.class)}
      >
        <div class="gn-adapters-page__header">
          <h1 class="gn-adapters-page__title">Adapters</h1>
          <p class="gn-adapters-page__subtitle">
            Manage your infrastructure adapters - database, auth, AI, storage,
            payments, and admin.
          </p>
        </div>

        <div class="gn-adapters-summary">
          <div class="gn-adapters-summary__card">
            <p class="gn-adapters-summary__label">Total</p>
            <p class="gn-adapters-summary__value">{ADAPTER_DOMAINS.length}</p>
            <p class="gn-adapters-summary__desc">adapter domains</p>
          </div>
          <div class="gn-adapters-summary__card gn-adapters-summary__card--success">
            <p class="gn-adapters-summary__label">Connected</p>
            <p class="gn-adapters-summary__value">{connected()}</p>
            <p class="gn-adapters-summary__desc">active adapters</p>
          </div>
          <Show when={errors() > 0}>
            <div class="gn-adapters-summary__card gn-adapters-summary__card--error">
              <p class="gn-adapters-summary__label">Errors</p>
              <p class="gn-adapters-summary__value">{errors()}</p>
              <p class="gn-adapters-summary__desc">need attention</p>
            </div>
          </Show>
        </div>

        <Show when={registryEntries().length > 0}>
          <section class="gn-adapters-registry">
            <div class="gn-adapters-registry__header">
              <div>
                <p class="gn-adapters-registry__label">Adapter Registry</p>
                <p class="gn-adapters-registry__desc">
                  Live adapter registry entries.
                </p>
              </div>
              <div class="gn-adapters-registry__stats">
                <span>{registryEntries().length} entries</span>
              </div>
            </div>
            <div class="gn-adapters-registry__grid">
              <For each={registryEntries()}>
                {(entry) => (
                  <div class="gn-adapters-registry__item">
                    <div class="gn-adapters-registry__info">
                      <p class="gn-adapters-registry__domain">{entry.domain}</p>
                      <p class="gn-adapters-registry__provider">
                        {entry.provider}
                      </p>
                    </div>
                    <AdapterStatusBadge status={entry.status} />
                  </div>
                )}
              </For>
            </div>
          </section>
        </Show>

        <div class="gn-adapters-filters">
          <button
            type="button"
            class={cx(
              "gn-adapters-filter-btn",
              !filterStatus() && "gn-adapters-filter-btn--active",
            )}
            onClick={() => setFilterStatus(undefined)}
            aria-pressed={!filterStatus()}
          >
            All
          </button>
          <For each={[...ADAPTER_STATUSES]}>
            {(s) => (
              <button
                type="button"
                class={cx(
                  "gn-adapters-filter-btn",
                  filterStatus() === s && "gn-adapters-filter-btn--active",
                )}
                onClick={() => setFilterStatus(s)}
                aria-pressed={filterStatus() === s}
              >
                <AdapterStatusBadge status={s} showLabel={false} /> {s}
              </button>
            )}
          </For>
        </div>

        <AdapterList
          onSelect={(domain) => {
            if (props.onNavigateDetail) props.onNavigateDetail(domain);
            else setSelectedDomain(domain);
          }}
          filterStatus={filterStatus()}
        />

        <Show when={selectedDomain()}>
          {(domain) => (
            <div class="gn-adapters-modal-overlay">
              <button
                type="button"
                aria-label="Close adapter configuration"
                class="gn-adapters-modal-backdrop"
                onClick={() => setSelectedDomain(null)}
              />
              <div
                ref={(element) => {
                  modalRef = element;
                }}
                class="gn-adapters-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby={modalTitleId}
                tabIndex={-1}
              >
                <AdapterConfigForm
                  domain={domain()}
                  titleId={modalTitleId}
                  initialConfig={props.configByDomain?.[domain()]}
                  onSave={async (config) => {
                    // Forward the submitted config to the consumer before
                    // closing the modal — matches the SolidJS reference.
                    await props.onConfigure?.(domain(), config);
                    setSelectedDomain(null);
                  }}
                  onCancel={() => setSelectedDomain(null)}
                />
              </div>
            </div>
          )}
        </Show>
      </div>
    </Show>
  );
}
